// Coquille de l'application terrain (zone /terrain, D-040) : session de l'agent (session serveur
// existante, prolongée par une session locale de trente jours protégée par un code à six chiffres,
// D-029), en-tête avec l'état de la file, bascule jour et nuit, navigation basse (Mon travail,
// Conflits, Réglages), bandeaux hors connexion, simulateur, reconnexion et mise à jour, service
// worker enregistré ici seulement.
import { PREFERENCES_PAR_DEFAUT, type Preferences } from '@cc/contrats/ui-preferences';
import { Bandeau, Bouton, IndicateurEnvironnement, Squelette, ecrireStockage, lireStockage } from '@cc/ui';
import { ClipboardList, Moon, Settings, Sun, TriangleAlert } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { appliquerPreferences } from '../../preferences/FournisseurPreferences';
import { useSession } from '../../session/FournisseurSession';
import { ETAT_PWA_INITIAL, ecouterInstallation, enregistrerServiceWorker, type EtatPwa } from '../../terrain/pwa';
import { estDeverrouille, ouvrirSessionLocale, sessionLocaleValide } from '../../terrain/session-locale';
import { lireSessionLocale, type SessionLocale } from '../../terrain/stockage';
import { synchroniseurPartage, type EtatSynchronisation } from '../../terrain/synchronisation';
import { useEnLigne, useRepliAutomatique, useSynchronisation } from '../../terrain/useTerrain';
import { PageCodeLocal } from './PageCodeLocal';
import styles from './terrain.module.css';

const CLE_THEME = 'cc-preferences:dernieres';

export interface ValeurZoneTerrain {
  session: SessionLocale;
  /** Session serveur non rouverte (hors connexion ou jeton expiré) : lecture et saisie sur l'appareil. */
  modeLocal: boolean;
  pwa: EtatPwa;
  rafraichirSession: () => Promise<void>;
}

const ContexteZone = createContext<ValeurZoneTerrain | null>(null);

export function useZoneTerrain(): ValeurZoneTerrain {
  const v = useContext(ContexteZone);
  if (!v) throw new Error('useZoneTerrain hors de CoquilleTerrain');
  return v;
}

function BasculeTheme() {
  const [theme, setTheme] = useState<Preferences['theme']>(() => lireStockage<Preferences>(CLE_THEME)?.theme ?? 'systeme');
  const sombre = theme === 'nuit' || (theme !== 'jour' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  const basculer = () => {
    const suivant: Preferences['theme'] = sombre ? 'jour' : 'nuit';
    const memorisees = { ...PREFERENCES_PAR_DEFAUT, ...(lireStockage<Preferences>(CLE_THEME) ?? {}), theme: suivant };
    appliquerPreferences(memorisees);
    ecrireStockage(CLE_THEME, { theme: memorisees.theme, densite: memorisees.densite, taille_police: memorisees.taille_police });
    setTheme(suivant);
  };
  return (
    <button type="button" className={styles.boutonIcone} aria-label={sombre ? 'Passer en mode jour' : 'Passer en mode nuit'} onClick={basculer}>
      {sombre ? <Sun aria-hidden strokeWidth={1.8} width={18} height={18} /> : <Moon aria-hidden strokeWidth={1.8} width={18} height={18} />}
    </button>
  );
}

/** Point d'état de la file dans l'en-tête : à jour, en attente, hors connexion ou conflit. */
function PointEtat({ sync, enLigne }: { sync: EtatSynchronisation; enLigne: boolean }) {
  const attente = sync.attente.operations + sync.attente.photos;
  const probleme = sync.attente.conflits > 0 || sync.reconnexionRequise || Boolean(sync.derniereErreur);
  const libelle = sync.enCours ? 'Synchronisation en cours' : sync.attente.conflits > 0 ? `${sync.attente.conflits} en conflit` : sync.reconnexionRequise ? 'Reconnexion requise' : !enLigne ? (attente > 0 ? `Hors connexion, ${attente} en attente` : 'Hors connexion') : attente > 0 ? `${attente} en attente` : 'À jour';
  const classe = probleme ? styles.pointAttention : !enLigne || attente > 0 || sync.enCours ? styles.pointInfo : styles.pointOk;
  return (
    <Link to="/terrain/reglages" className={styles.etatFile} aria-label={`État de la synchronisation : ${libelle}`}>
      <span className={`${styles.point} ${classe}`} aria-hidden />
      <span aria-hidden>{libelle}</span>
    </Link>
  );
}

interface Entree {
  id: string;
  libelle: string;
  href: string;
  icone: ComponentType<{ 'aria-hidden'?: boolean; strokeWidth?: number }>;
}

const ENTREES: Entree[] = [
  { id: 'travail', libelle: 'Mon travail', href: '/terrain', icone: ClipboardList },
  { id: 'conflits', libelle: 'Conflits', href: '/terrain/conflits', icone: TriangleAlert },
  { id: 'reglages', libelle: 'Réglages', href: '/terrain/reglages', icone: Settings },
];

function entreeActive(chemin: string): string {
  if (chemin.startsWith('/terrain/conflits')) return 'conflits';
  if (chemin.startsWith('/terrain/reglages')) return 'reglages';
  return 'travail';
}

function Coquille({ children }: { children: ReactNode }) {
  const { session, modeLocal, pwa } = useZoneTerrain();
  const location = useLocation();
  const enLigne = useEnLigne();
  const sync = useSynchronisation();
  const actif = entreeActive(location.pathname);
  return (
    <div className={styles.coquille}>
      <a className="cc-evitement" href="#contenu-principal">
        Aller au contenu
      </a>
      <header className={styles.entete}>
        <Link to="/terrain" className={styles.marque}>
          <img src="/marque-collectivitesconnect.png" alt="" width={26} height={22} className="cc-logo-jour" />
          <img src="/marque-collectivitesconnect-fond-sombre.png" alt="" width={26} height={22} className="cc-logo-nuit" />
          <span className={styles.marqueTexte}>
            <strong>Terrain</strong>
            <small>{session.collectivite ?? 'CollectivitésConnect'}</small>
          </span>
        </Link>
        <div className={styles.actionsEntete}>
          <PointEtat sync={sync} enLigne={enLigne} />
          <BasculeTheme />
        </div>
      </header>
      <nav className={styles.navBasse} aria-label="Navigation de l'application terrain">
        <ul>
          {ENTREES.map((e) => {
            const Icone = e.icone;
            const compteur = e.id === 'conflits' ? sync.attente.conflits : 0;
            return (
              <li key={e.id}>
                <Link to={e.href} className={styles.entreeBasse} aria-current={e.id === actif ? 'page' : undefined} aria-label={compteur > 0 ? `${e.libelle}, ${compteur}` : undefined}>
                  <Icone aria-hidden strokeWidth={1.8} />
                  {e.libelle}
                  {compteur > 0 ? (
                    <span className={styles.pastille} aria-hidden>
                      {compteur}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <main id="contenu-principal" className={styles.contenu} tabIndex={-1}>
        {!enLigne ? <Bandeau type="horsligne">Hors connexion : vos rapports, constats et photos sont enregistrés sur cet appareil et seront synchronisés au retour du réseau.</Bandeau> : null}
        {enLigne && (modeLocal || sync.reconnexionRequise) ? (
          <Bandeau type="attention" titre="Session serveur à rouvrir">
            Les données de cet appareil restent lisibles et modifiables ; reconnectez-vous pour synchroniser.{' '}
            <Link to="/connexion" state={{ depuis: '/terrain' }}>
              Se reconnecter
            </Link>
          </Bandeau>
        ) : null}
        {sync.source === 'simulateur' ? <Bandeau type="info">Simulateur local de l&apos;API terrain : missions, tournées et ordres fictifs de présentation, aucune donnée n&apos;est envoyée à un serveur.</Bandeau> : null}
        {pwa.miseAJourDisponible ? (
          <Bandeau type="info" titre="Nouvelle version de l'application prête">
            <Bouton petit variante="secondaire" onClick={() => window.location.reload()}>
              Recharger
            </Bouton>
          </Bandeau>
        ) : null}
        {children}
      </main>
      <footer className={styles.pied}>
        <span>{session.nom}</span>
        <span>Application terrain</span>
        <IndicateurEnvironnement environnement={import.meta.env.VITE_CC_ENVIRONNEMENT} />
      </footer>
    </div>
  );
}

function Attente({ libelle }: { libelle: string }) {
  return (
    <div className={styles.pageVerrou}>
      <Squelette lignes={4} libelle={libelle} />
    </div>
  );
}

/** Garde de la zone : session serveur ou session locale valide, code local défini puis saisi. */
export function CoquilleTerrain() {
  const { etat, contexte, chargementContexte, reprendre } = useSession();
  const location = useLocation();
  const enLigne = useEnLigne();
  const [locale, setLocale] = useState<{ chargee: boolean; session: SessionLocale | null }>({ chargee: false, session: null });
  const [deverrouille, setDeverrouille] = useState(false);
  const [pwa, setPwa] = useState<EtatPwa>(ETAT_PWA_INITIAL);
  const majPwa = useCallback((p: Partial<EtatPwa>) => setPwa((e) => ({ ...e, ...p })), []);

  useEffect(() => {
    let actif = true;
    void lireSessionLocale().then((s) => actif && setLocale({ chargee: true, session: s }));
    return () => {
      actif = false;
    };
  }, []);

  // Session serveur ouverte avec un espace actif : la session locale est ouverte ou renouvelée.
  useEffect(() => {
    if (etat !== 'connecte' || !contexte?.appartenance_active) return;
    let actif = true;
    void ouvrirSessionLocale(contexte).then(({ session, compteChange }) => {
      if (!actif) return;
      setLocale({ chargee: true, session });
      if (compteChange) setDeverrouille(false);
      void synchroniseurPartage().rafraichirEtat();
    });
    return () => {
      actif = false;
    };
  }, [etat, contexte]);

  // Retour du réseau en mode local : la session serveur est rouverte par le cookie de rafraîchissement.
  useEffect(() => {
    if (!enLigne || etat !== 'deconnecte' || !sessionLocaleValide(locale.session)) return;
    void reprendre().then((ok) => {
      if (ok) void synchroniseurPartage().synchroniser().catch(() => undefined);
    });
  }, [enLigne, etat, locale.session, reprendre]);

  useEffect(() => {
    void enregistrerServiceWorker(majPwa);
    return ecouterInstallation(majPwa);
  }, [majPwa]);

  const sessionValide = sessionLocaleValide(locale.session);
  useRepliAutomatique(sessionValide);

  const rafraichirSession = useCallback(async () => {
    setLocale({ chargee: true, session: await lireSessionLocale() });
  }, []);

  if (etat === 'initialisation' || !locale.chargee) return <Attente libelle="Ouverture de la session" />;
  if (etat === 'deconnecte' && !sessionValide) return <Navigate to="/connexion" replace state={{ depuis: `${location.pathname}${location.search}` }} />;
  if (etat === 'connecte') {
    if (contexte?.blocage?.code === 'mfa_requis') return <Navigate to="/mfa/activer" replace state={{ appartenanceId: contexte.appartenance_active?.id }} />;
    if (contexte && (!contexte.appartenance_active || contexte.blocage)) return <Navigate to="/contexte" replace />;
    if ((chargementContexte || !contexte) && !sessionValide) return <Attente libelle="Chargement de l'espace de travail" />;
  }
  const session = locale.session;
  if (!session) return <Attente libelle="Préparation de l'appareil" />;
  const modeLocal = etat !== 'connecte';
  const valeur: ValeurZoneTerrain = { session, modeLocal, pwa, rafraichirSession };
  if (!session.code) {
    return (
      <ContexteZone.Provider value={valeur}>
        <PageCodeLocal
          mode="definir"
          onFait={() => {
            setDeverrouille(true);
            void rafraichirSession();
          }}
        />
      </ContexteZone.Provider>
    );
  }
  if (!deverrouille && !estDeverrouille(session.compte_id)) {
    return (
      <ContexteZone.Provider value={valeur}>
        <PageCodeLocal mode="saisir" onFait={() => setDeverrouille(true)} />
      </ContexteZone.Provider>
    );
  }
  return (
    <ContexteZone.Provider value={valeur}>
      <Coquille>
        <Outlet />
      </Coquille>
    </ContexteZone.Provider>
  );
}
